import { useAuth } from '@clerk/nextjs';
import { ObjectHelper } from '@firmable/utilities';
import { useQuery } from '@tanstack/react-query';
import { Query } from '@web/constants/query';
import * as App from '@web/contracts/App';
import { ActivePlanWithoutCountry } from '@web/contracts/App/PlanBucket';
import axios from 'axios';
import { hasSubscription } from 'guards/Subscription';
import React from 'react';
import { useProducts } from './useProducts';
import { useSubscriptions } from './useSubscriptions';

const isActiveProductOnly = (product: Product): product is ActiveProduct => {
  return 'bucket_id' in product && typeof product.bucket_id === 'string';
};

type ActiveProduct = {
  bucket_id: string;
  stripe_product_id: string;
} & (
  | {
      monthly: App.ActivePlan[BillingCountry]['monthly'];
      yearly?: never; // both can't be present at the same time
    }
  | {
      yearly: App.ActivePlan[BillingCountry]['yearly'];
      monthly?: never; // both can't be present at the same time
    }
);

type AllProducts = Record<string, ActivePlanWithoutCountry>;

type Product = ActiveProduct | AllProducts;

export interface ApiResponse {
  success: true;
  plan: Product;
}

export type QueryResponse = AllProducts | undefined;

/**
 * React Query hook to fetch all the available products for update plan
 * @returns QueryResponse
 */
const useProductsForUpdate = () => {
  const { isSignedIn } = useAuth();

  const { data: subscriptions, isLoading } = useSubscriptions();
  const { data: currentProducts, isLoading: isProductsLoading } = useProducts();

  const currentPlanBucketId = React.useMemo(() => {
    if (!currentProducts) return null;

    const firstProductId = Object.keys(currentProducts.products)[0];

    return currentProducts.products[firstProductId]?.bucket_id;
  }, [currentProducts]);

  const customerSubscription = React.useMemo(() => {
    if (!hasSubscription(subscriptions)) return null;

    const bucketId = subscriptions.subscription.planBucketId;
    const isTrial = subscriptions.subscription.status === 'trialing';
    const billingCountry = subscriptions.subscription.billingCountry;

    return { bucketId, isTrial, billingCountry };
  }, [subscriptions]);

  const products = currentProducts?.products;

  const productsOfCurrentBillingCountry = React.useMemo(() => {
    if (!products || !customerSubscription) return {};

    return ObjectHelper.getKeys(products).reduce(
      (acc, productId) => {
        const product = products[productId];
        const { AU, NZ, US, ...rest } = product;

        acc[productId] = {
          ...rest,
          ...product[customerSubscription.billingCountry],
        };

        return acc;
      },
      {} as Record<string, ActivePlanWithoutCountry>,
    );
  }, [products, customerSubscription]);

  return useQuery<QueryResponse>({
    queryKey: [Query.StripeKeys.productsForUpdate, { ...customerSubscription }],
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!isSignedIn && !isLoading && !isProductsLoading && !!customerSubscription,
    staleTime: Infinity,
    queryFn: async () => {
      // if bucket is same then return the current products
      if (currentPlanBucketId === customerSubscription?.bucketId) {
        return productsOfCurrentBillingCountry;
      }

      /**
       * - If customer is on trial then we need to return all products to make sure when
       *   they want to change plan we show customer's current pricing bucket plans
       * - If customer is on active plan then we need to return the active plan product only
       */
      const response = await axios.get<ApiResponse>(`${process.env.NEXT_PUBLIC_API_APP_ENDPOINT}/customer/products`, {
        params: {
          return: customerSubscription?.isTrial ? undefined : 'active-plan',
          bucketId: customerSubscription?.isTrial ? customerSubscription?.bucketId : undefined,
          billingCountry: customerSubscription?.billingCountry,
        },
      });

      if (isActiveProductOnly(response.data.plan)) {
        const { bucket_id: _, stripe_product_id, ...product } = response.data.plan;
        if (!products) return products;
        /**
         * replace the current product's plan with the customer's current plan
         * Use case: when FBL plan bucket is different than the customer's current plan bucket
         * and customer wants to change the plan(only increase user qty within existing plan)
         * If customer is on monthly then it will be replaced with the monthly plan data
         * If customer is on yearly then it will be replaced with the yearly plan data
         */
        productsOfCurrentBillingCountry[stripe_product_id] = {
          ...productsOfCurrentBillingCountry?.[stripe_product_id],
          ...product,
        };

        return productsOfCurrentBillingCountry;
      }

      return response.data.plan;
    },
  });
};

export { useProductsForUpdate };
